import React, { useMemo } from 'react'
import { useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'
import urlParser from 'js-video-url-parser'

import { HOST_NAME } from '../../data/config/url'

import { XStack, YStack } from 'tamagui'

import { Link } from '../Link'
import { H3, Text } from '../Text'
import { Card } from '../Card'
import { CoverPhotoWithBlurFill } from '../CoverPhoto/CoverPhotoWithBlurFill'
import { FormattedMarkdown } from '../FormattedMarkdown/FormattedMarkdown'
import { UserPrimaryPhoto, UserDefaultPhoto } from '../User/UserPrimaryPhoto'

import { PresentationCardNew_presentation$key } from './__generated__/PresentationCardNew_presentation.graphql'

const ALLOWED_ELEMENTS = ['p']

export const PresentationCard = Link.styleable<{
  presentation: PresentationCardNew_presentation$key
  titleNumberOfLines?: number
  numberOfLines?: number
  showCoverPhoto?: boolean
  href?: string
}>(
  (
    {
      presentation,
      titleNumberOfLines = 2,
      numberOfLines = 8,
      showCoverPhoto = true,
      ...props
    },
    ref
  ) => {
    const { t } = useLingui()
    const data = useFragment(
      graphql`
        fragment PresentationCardNew_presentation on Presentation {
          rowId
          description
          prettyUrl
          title
          updatedAt

          videoSourceUrl

          presenterFirstName
          presenterLastName

          presenter {
            firstName
            lastName
            ...UserPrimaryPhotoNew_user
          }

          # Signed URL for guest presenter avatar (when no linked User)
          guestPresenterAvatarUrl(transform: { w: 32, h: 32, dpr: 3 })
        }
      `,
      presentation
    )

    const prettyUrl = data.prettyUrl ?? data.rowId
    const updatedAt = data.updatedAt ?? ''

    const coverPhotoSrc = useMemo(() => {
      if (data.videoSourceUrl) {
        const videoInfo = urlParser.parse(data.videoSourceUrl)
        if (videoInfo?.provider === 'youtube') {
          const photoUrl = urlParser.create({
            format: 'longImage',
            params: { imageQuality: 'maxresdefault' },
            videoInfo,
          })
          if (photoUrl) return photoUrl
        }
      }
      return `${HOST_NAME}/social/presentation/${prettyUrl}/card.svg?${updatedAt}`
    }, [data.videoSourceUrl, prettyUrl, updatedAt])
    const title = data.title ?? ''

    return (
      <Link
        {...props}
        ref={ref}
        href={`/presentations/${prettyUrl}`}
        aria-label={t`Link to ${title} Presentation`}
        display='flex'
        flexDirection='column'
        hoverStyle={{ textDecorationLine: 'none' }}
      >
        {showCoverPhoto && (
          <CoverPhotoWithBlurFill
            alt={`Cover Photo for ${title}`}
            src={coverPhotoSrc}
            maxHeight={200}
            borderTopLeftRadius='$8'
            borderTopRightRadius='$8'
          />
        )}

        <Card
          glass
          gap='$4'
          padding='$4'
          borderRadius='$8'
          borderTopLeftRadius={showCoverPhoto ? 0 : undefined}
          borderTopRightRadius={showCoverPhoto ? 0 : undefined}
          flex={props.flex}
          style={{
            willChange: 'box-shadow',
            transition: 'all 375ms cubic-bezier(0,0.5,1,1)',
          }}
          hoverStyle={{
            // @ts-expect-error boxShadow is web-only
            boxShadow:
              'rgba(10, 37, 64, 0.16) 0px 8px 16px -4px, rgba(10, 37, 64, 0.12) 0px 10px 30px -7px',
          }}
        >
          <H3 size='$6' numberOfLines={titleNumberOfLines}>
            {title}
          </H3>

          {!!data.description && (
            <YStack flex={props.flex}>
              <FormattedMarkdown
                allowedElements={ALLOWED_ELEMENTS}
                formattedVariant='PLAIN_TEXT'
                numberOfLines={numberOfLines}
                textSize='$4'
              >
                {data.description}
              </FormattedMarkdown>
            </YStack>
          )}

          {data.presenter ? (
            <XStack alignItems='center' gap='$3'>
              <UserPrimaryPhoto user={data.presenter} size='$8' />
              <Text size='$4' numberOfLines={1}>
                {data.presenter.firstName} {data.presenter.lastName}
              </Text>
            </XStack>
          ) : (
            <XStack alignItems='center' gap='$3'>
              <UserDefaultPhoto
                defaultAvatarUrl={data.guestPresenterAvatarUrl}
                width={32}
                height={32}
                borderRadius={16}
              />
              <Text size='$4' numberOfLines={1}>
                {data.presenterFirstName} {data.presenterLastName}
              </Text>
            </XStack>
          )}
        </Card>
      </Link>
    )
  }
)
